import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const BrandingCtx = createContext(null);

const DEFAULT_BRAND = {
  display_name: "MangoCubs",
  tagline: "Smart Solutions for Early Learning",
  logo_url: "/mangocubs-logo.png?v=20260514c",
  favicon_url: "/icon-192.png?v=20260514c",
  primary_color: "#F58A1F",
  accent_color: "#7CB342",
  text_color: "#3D2412",
  phone: "+919844139333",
  email: "mangocubs.edu@gmail.com",
  website: "https://factskoot.com",
  whatsapp_number: "+919844139333",
  powered_by: "MangoCubs",
};

function getSubdomain() {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname;
  // Skip localhost, IPs, and preview/emergent platform domains
  if (/^(localhost|127\.|\d+\.\d+\.\d+\.\d+)/.test(host)) return null;
  if (host.includes("emergentagent.com") || host.includes("preview")) return null;
  const parts = host.split(".");
  if (parts.length < 3) return null;
  const sub = parts[0];
  if (["www", "app", "api", "demo"].includes(sub)) return null;
  return sub;
}

function applyBrandToDom(brand) {
  if (typeof document === "undefined") return;
  // CSS variables for runtime colour overrides
  const root = document.documentElement;
  if (brand.primary_color) root.style.setProperty("--brand-primary", brand.primary_color);
  if (brand.accent_color) root.style.setProperty("--brand-accent", brand.accent_color);
  if (brand.text_color) root.style.setProperty("--brand-text", brand.text_color);
  // Document title
  const name = brand.display_name || "MangoCubs";
  document.title = name === "MangoCubs"
    ? "MangoCubs · Preschool OS"
    : `${name} · Powered by MangoCubs`;
  // Favicon
  if (brand.favicon_url) {
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = brand.favicon_url;
  }
}

export function BrandingProvider({ children }) {
  const [brand, setBrand] = useState(DEFAULT_BRAND);
  const [loaded, setLoaded] = useState(false);
  const { user } = useAuth();

  const refresh = useCallback(async () => {
    // 1) Try subdomain-based lookup (public, no auth needed)
    const sub = getSubdomain();
    if (sub) {
      try {
        const { data } = await api.get(`/branding/by-subdomain/${sub}`);
        setBrand({ ...DEFAULT_BRAND, ...data });
        applyBrandToDom({ ...DEFAULT_BRAND, ...data });
        setLoaded(true);
        return;
      } catch {
        // fall through to user-based lookup
      }
    }
    // 2) Try user's branch branding (requires auth)
    try {
      const { data } = await api.get("/branding/current");
      setBrand({ ...DEFAULT_BRAND, ...data });
      applyBrandToDom({ ...DEFAULT_BRAND, ...data });
    } catch {
      // 3) Default MangoCubs
      setBrand(DEFAULT_BRAND);
      applyBrandToDom(DEFAULT_BRAND);
    } finally {
      setLoaded(true);
    }
  }, []);

  // Re-fetch branding whenever the auth user changes (login / logout / role switch)
  // so the first authenticated paint already shows the co-brand, no flicker.
  useEffect(() => {
    refresh();
  }, [refresh, user?.user_id, user?.branch_id]);

  const isCoBranded = brand?.display_name && brand.display_name !== "MangoCubs";

  return (
    <BrandingCtx.Provider value={{ brand, loaded, isCoBranded, refresh }}>
      {children}
    </BrandingCtx.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingCtx) || { brand: DEFAULT_BRAND, isCoBranded: false, refresh: () => {} };
}
